import { useEffect, useCallback, useMemo } from 'react';
import { Task } from '../../../../../types/task';

/**
 * 专注任务逻辑工具类
 * 提供专注模式的状态计算和业务逻辑
 */
export class FocusTaskLogic {
  /**
   * 格式化剩余时间为 MM:SS 格式
   */
  static formatRemainingTime(ms: number | null): string {
    if (ms === null || ms <= 0) return '00:00';
    const totalSeconds = Math.max(0, Math.floor(ms / 1000));
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  }

  /**
   * 判断任务是否可拖动进度
   */
  static isTaskProgressDraggable(task: Task): boolean {
    return !(task.startAt && task.cost);
  }

  /**
   * 计算当前显示的进度
   */
  static calculateCurrentProgress(
    isDraggingProgressBar: boolean,
    localDragProgress: number | null,
    isTaskProgressDraggable: boolean,
    getTaskProgress: (taskId: string) => number,
    taskId: string
  ): number {
    if (isDraggingProgressBar && localDragProgress !== null && isTaskProgressDraggable) {
      return localDragProgress;
    }
    return getTaskProgress(taskId);
  }

  /**
   * 判断动态计时器是否已完成
   */
  static isTimerComplete(
    isPlaying: boolean,
    isTaskProgressDraggable: boolean,
    userProgressMap: Record<string, number>,
    taskId: string,
    currentProgress: number
  ): boolean {
    return isPlaying &&
           isTaskProgressDraggable &&
           userProgressMap[taskId] === undefined &&
           currentProgress >= 100;
  }

  /**
   * 创建播放控制事件处理器
   */
  static createPlayControlHandlers(
    task: Task,
    startSession: (data: { taskId: string; durationMs: number }) => void,
    removePlayingTask: (taskId: string) => void,
    setFocusedTask: (taskId: string | null) => void,
    setShowDurationPopover: (show: boolean) => void,
    trackEvent: (event: string, data: any) => void,
    ephemeralSessions: Record<string, any>,
    isFocused: boolean
  ) {
    const handlePlayClick = (e: React.MouseEvent) => {
      e.stopPropagation();
      startSession({ taskId: task.id, durationMs: 10 * 60 * 1000 });
      trackEvent('focus task started', {
        task_id: task.id,
        task_title: task.title.substring(0, 50),
        duration_minutes: 10
      });
      setShowDurationPopover(false);
    };

    const handleDurationSelect = (durationMinutes: number) => {
      startSession({ taskId: task.id, durationMs: durationMinutes * 60 * 1000 });
      trackEvent('focus task started', {
        task_id: task.id,
        task_title: task.title.substring(0, 50),
        duration_minutes: durationMinutes
      });
      setShowDurationPopover(false);
    };

    const handlePauseClick = (e: React.MouseEvent) => {
      e.stopPropagation();
      removePlayingTask(task.id);
      if (isFocused) {
        setFocusedTask(null);
      }
      trackEvent('focus task stopped', {
        task_id: task.id,
        task_title: task.title.substring(0, 50)
      });
      setShowDurationPopover(false);
    };

    const handleRestartClick = (e: React.MouseEvent) => {
      e.stopPropagation();

      const session = ephemeralSessions[task.id];
      const durationMs = session ? session.durationMs : 10 * 60 * 1000;

      startSession({ taskId: task.id, durationMs });

      trackEvent('focus task restarted', {
        task_id: task.id,
        task_title: task.title.substring(0, 50),
        duration_minutes: Math.floor(durationMs / (60 * 1000))
      });

      setShowDurationPopover(false);
    };

    return {
      handlePlayClick,
      handleDurationSelect,
      handlePauseClick,
      handleRestartClick
    };
  }

  /**
   * 创建弹窗控制事件处理器
   */
  static createPopoverHandlers(
    isPlaying: boolean,
    playButtonRef: React.RefObject<HTMLButtonElement>,
    setPopoverPosition: (pos: { top: number; left: number } | null) => void,
    setShowDurationPopover: (show: boolean) => void,
    popoverTimeoutRef: React.MutableRefObject<NodeJS.Timeout | null>
  ) {
    const handleMouseEnterPlayButton = () => {
      if (popoverTimeoutRef.current) clearTimeout(popoverTimeoutRef.current);
      if (!isPlaying && playButtonRef.current) {
        const rect = playButtonRef.current.getBoundingClientRect();
        setPopoverPosition({
          top: rect.top - 10,
          left: rect.left + rect.width / 2,
        });
        setShowDurationPopover(true);
      }
    };

    const handleMouseLeavePlayButtonContainer = () => {
      popoverTimeoutRef.current = setTimeout(() => {
        setShowDurationPopover(false);
      }, 200);
    };

    const handleMouseEnterPopover = () => {
      if (popoverTimeoutRef.current) clearTimeout(popoverTimeoutRef.current);
    };

    const handleMouseLeavePopover = () => {
      setShowDurationPopover(false);
    };

    return {
      handleMouseEnterPlayButton,
      handleMouseLeavePlayButtonContainer,
      handleMouseEnterPopover,
      handleMouseLeavePopover
    };
  }
}

/**
 * 专注任务定时器Hook
 * 处理定时器的启动和清理
 */
export const useFocusTimer = (
  isPlaying: boolean,
  isTaskProgressDraggable: boolean,
  userProgressMap: Record<string, number>,
  taskId: string,
  getRemainingTime: (taskId: string) => number | null,
  forceUpdate: React.Dispatch<React.SetStateAction<number>>
) => {
  useEffect(() => {
    let intervalId: NodeJS.Timeout | undefined;
    const sessionRemainingTime = getRemainingTime(taskId);
    const isEligibleForTimer = isPlaying &&
                               isTaskProgressDraggable &&
                               userProgressMap[taskId] === undefined &&
                               (sessionRemainingTime === null || sessionRemainingTime > 0);

    if (isEligibleForTimer) {
      intervalId = setInterval(() => {
        forceUpdate(prev => prev + 1);
      }, 1000);
    }

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [isPlaying, isTaskProgressDraggable, userProgressMap, taskId, forceUpdate, getRemainingTime]);
};
